import { useNavigate } from 'react-router';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { Tags } from 'lucide-react';
import { TaxonomyStatusBadge, type TaxonomySummary } from '@/entities/taxonomy';
import { ROUTES } from '@/shared/config/routes';
import {
  ROW_WITHOUT_FOCUS_RING,
  TableIconBadge,
  redesignTableStyles,
} from '@/shared/ui/data-table';
import { TaxonomyActionMenu } from '../TaxonomyActionMenu/TaxonomyActionMenu';

interface TaxonomyTableProps {
  taxonomies: TaxonomySummary[];
  isLoading: boolean;
  /** Selected ids for bulk actions; omit to hide the checkbox column. */
  selectedIds?: Set<string>;
  onToggleRow?: (id: string) => void;
  onToggleAll?: () => void;
  /** Passed to each row's action menu, which is where the write actions live. */
  canManage: boolean;
}

const EM_DASH = '—';
const SKELETON_ROW_COUNT = 5;
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

/** Human label for the stored scope value. */
function scopeLabel(scope: string): string {
  if (scope === 'organization') return i18n.t('knowledge:taxonomy.taxonomyTable.org');
  if (scope === 'workspace') return i18n.t('knowledge:taxonomy.taxonomyTable.workspace');
  return scope;
}

/** Joins a value list for a cell, falling back to an em-dash when empty. */
function joinOrDash(values: string[]): string {
  return values.length > 0 ? values.join(' · ') : EM_DASH;
}

function TaxonomyTableRow({
  taxonomy,
  selected,
  onToggleRow,
  canManage,
}: {
  taxonomy: TaxonomySummary;
  selected: boolean;
  onToggleRow?: (id: string) => void;
  canManage: boolean;
}) {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const modifiedBy = taxonomy.creator?.name ?? null;

  const handleRowClick = () => {
    void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_DETAIL(taxonomy.id));
  };

  return (
    <DatatableRow
      interactive
      data-testid="taxonomy-row"
      className={`h-[72px] ${ROW_WITHOUT_FOCUS_RING}`}
      onClick={handleRowClick}
    >
      {onToggleRow && (
        <DatatableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleRow(taxonomy.id)}
            aria-label={t('taxonomy.taxonomyTable.selectSchema', { name: taxonomy.name })}
          />
        </DatatableCell>
      )}
      <DatatableCell>
        <div className="flex items-center gap-3">
          <TableIconBadge>
            <Tags className="size-5 text-foreground" aria-hidden="true" />
          </TableIconBadge>
          <span className="text-sm font-medium text-foreground">{taxonomy.name}</span>
        </div>
      </DatatableCell>
      {/* Version as its own column, matching Lists — it reads with Status
          rather than crowding the name. */}
      <DatatableCell>
        <span className="text-sm text-foreground">
          {taxonomy.currentVersionNumber != null ? `v${taxonomy.currentVersionNumber}` : EM_DASH}
        </span>
      </DatatableCell>
      <DatatableCell data-testid="taxonomy-status">
        <TaxonomyStatusBadge status={taxonomy.status} />
      </DatatableCell>
      <DatatableCell>
        <span className="text-sm text-foreground">{scopeLabel(taxonomy.scope)}</span>
      </DatatableCell>
      <DatatableCell>
        <span className="text-sm text-foreground">{joinOrDash(taxonomy.market)}</span>
      </DatatableCell>
      <DatatableCell>
        <span className="text-sm text-foreground">{joinOrDash(taxonomy.platform)}</span>
      </DatatableCell>
      <DatatableCell data-testid="taxonomy-updated-at">
        <div className="flex flex-col">
          <span className="text-sm text-foreground">
            {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to `format()` rather than copy */}
            {format(new Date(taxonomy.updatedAt), 'MMM d, yyyy')}
          </span>
          {modifiedBy && <span className="text-sm text-foreground">{modifiedBy}</span>}
        </div>
      </DatatableCell>
      <DatatableCell className="w-13">
        <TaxonomyActionMenu taxonomy={taxonomy} canManage={canManage} />
      </DatatableCell>
    </DatatableRow>
  );
}

function TaxonomyTableSkeletonRow({ withCheckbox }: { withCheckbox: boolean }) {
  return (
    <DatatableRow className="h-[72px]">
      {withCheckbox && (
        <DatatableCell>
          <Skeleton className="size-4 rounded" />
        </DatatableCell>
      )}
      <DatatableCell>
        <div className="flex items-center gap-3">
          <Skeleton className="size-9 rounded" />
          <Skeleton className="h-4 w-40" />
        </div>
      </DatatableCell>
      <DatatableCell>
        <Skeleton className="h-4 w-8" />
      </DatatableCell>
      <DatatableCell>
        <Skeleton className="h-5 w-20 rounded-full" />
      </DatatableCell>
      <DatatableCell>
        <Skeleton className="h-4 w-16" />
      </DatatableCell>
      <DatatableCell>
        <Skeleton className="h-4 w-24" />
      </DatatableCell>
      <DatatableCell>
        <Skeleton className="h-4 w-24" />
      </DatatableCell>
      <DatatableCell>
        <Skeleton className="h-4 w-20" />
      </DatatableCell>
      <DatatableCell className="w-13">
        <Skeleton className="size-9 rounded" />
      </DatatableCell>
    </DatatableRow>
  );
}

/**
 * Presentational taxonomy list table. Empty/error/no-results UX is owned by the
 * orchestrator. Columns mirror the prototype minus the "Fields" column (no
 * field-definitions model yet).
 */
export function TaxonomyTable({
  taxonomies,
  isLoading,
  selectedIds,
  onToggleRow,
  onToggleAll,
  canManage,
}: TaxonomyTableProps) {
  const { t } = useTranslation('knowledge');
  const isBusy = isLoading && taxonomies.length === 0;
  const allSelected =
    taxonomies.length > 0 && taxonomies.every((t) => selectedIds?.has(t.id) === true);

  return (
    <Datatable aria-busy={isBusy || undefined} className={redesignTableStyles.table}>
      <DatatableHeader>
        <DatatableRow className={ROW_WITHOUT_FOCUS_RING}>
          {onToggleAll && (
            <DatatableHead scope="col" className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onToggleAll}
                aria-label={
                  allSelected
                    ? t('taxonomy.taxonomyTable.clearSelection')
                    : t('taxonomy.taxonomyTable.selectAllSchemas')
                }
              />
            </DatatableHead>
          )}
          <DatatableHead scope="col">{t('taxonomy.taxonomyTable.name')}</DatatableHead>
          <DatatableHead scope="col" className="w-20">
            {t('taxonomy.taxonomyTable.version')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-32">
            {t('taxonomy.taxonomyTable.status')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-32">
            {t('taxonomy.taxonomyTable.scope')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-48">
            {t('taxonomy.taxonomyTable.market')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-56">
            {t('taxonomy.taxonomyTable.platforms')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-40">
            {t('taxonomy.taxonomyTable.lastModified')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-13">
            <span className="sr-only">{t('taxonomy.taxonomyTable.actions')}</span>
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {isBusy
          ? SKELETON_ROWS.map((i) => (
              <TaxonomyTableSkeletonRow key={i} withCheckbox={!!onToggleAll} />
            ))
          : taxonomies.map((taxonomy) => (
              <TaxonomyTableRow
                key={taxonomy.id}
                taxonomy={taxonomy}
                selected={selectedIds?.has(taxonomy.id) ?? false}
                onToggleRow={onToggleRow}
                canManage={canManage}
              />
            ))}
      </DatatableBody>
    </Datatable>
  );
}

TaxonomyTable.displayName = 'TaxonomyTable';
